import React, { useState, useRef, useEffect } from 'react';
import { Formik } from 'formik';
import { useTranslation } from 'next-i18next';
import { useDebounce } from 'react-use';
import Box from '@mui/material/Box';

import Divider from 'src/components/ui/Divider';
import Typography from 'src/components/ui/Typography';
import SimpleHorizontalUserCard from 'src/components/ui/SimpleHorizontalUserCard';
import Icon from 'src/components/ui/Icon';
import Loader from 'src/components/ui/Loader';
import { Tab, Tabs } from 'src/components/ui/Tabs';
import TextField from 'src/components/ui/TextField';

import { ContactStatus, type User, type UserProfileQuery } from 'src/types/__generated__/graphql';
import { colours } from 'src/constants/colours';
import { isBottomDiv } from 'src/utils/isBottom';
import { styledComponent } from 'src/utils/styled';
import type { ViewTypes } from 'src/components/profile/relations/RelationsModal';
import { useUserContactsQuery } from 'src/hooks/__generated__/queries';
import useUser from 'src/hooks/useUser';
import AddRemoveContact from 'src/components/ui/AddRemoveContact';
import useAlerts from 'src/hooks/useAlerts';

const StyledTabs = styledComponent(Tabs)`
    box-sizing: border-box;
    margin-top: 1rem;
`;

const TextFieldWrapper = styledComponent(Box)`
    margin-left: -1.5rem;
    width: calc(100% + 3rem);
    padding: 0 1.5rem;
    box-shadow: 0 0.625rem 1.25rem ${colours.blackOpacity.O08};
    z-index: 10;
`;

type Tab = 'common' | 'all';

interface Props {
    user: NonNullable<UserProfileQuery['userProfile']>;
    type: ViewTypes;
}

const defaultTake = 10;
const RelationsModalContent: React.FC<Props> = ({ user, type }) => {
    const { t } = useTranslation(['profile', 'common']);
    const currentUser = useUser();
    const { addAlert } = useAlerts();
    const [selectedTab, setSelectedTab] = useState<Tab>('common');
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const scrollerContainerRef = useRef<HTMLDivElement | null>(null);

    useDebounce(
        () => {
            setDebouncedQuery(query);
            if (scrollerContainerRef.current) {
                scrollerContainerRef.current.scrollTop = 0;
            }
        },
        500,
        [query],
    );

    const { data, fetchMore, loading } = useUserContactsQuery({
        variables: {
            userId: user.id,
            onlyMutual: selectedTab === 'common' && type !== 'searchProfile',
            take: defaultTake,
            skip: 0,
            query: debouncedQuery.length > 0 ? debouncedQuery : null,
        },
        notifyOnNetworkStatusChange: true,
    });

    const allDataLoaded = Boolean(
        data?.userContactsV2?.results?.length &&
            data.userContactsV2.total &&
            data.userContactsV2.results.length >= data.userContactsV2.total,
    );

    useEffect(() => {
        if (scrollerContainerRef.current) {
            scrollerContainerRef.current.scrollTop = 0;
        }
    }, [type]);

    const handleTabChange = (_ev: React.SyntheticEvent, tab: Tab) => {
        if (scrollerContainerRef.current) {
            scrollerContainerRef.current.scrollTop = 0;
        }
        setSelectedTab(tab);
    };

    const handleScroll = async (ev: React.UIEvent<HTMLDivElement, UIEvent>) => {
        const bottom = isBottomDiv(ev.currentTarget);
        if (bottom && !loading && !allDataLoaded) {
            try {
                await fetchMore({
                    variables: {
                        skip: data?.userContactsV2?.results?.length,
                    },
                });
            } catch (error: unknown) {
                addAlert({
                    type: 'error',
                    message: (error as Error).message,
                });
            }
        }
    };

    return (
        <Box mb={type === 'searchProfile' ? '1.5rem' : 0} display="flex" flexDirection="column" height="100%">
            {type === 'relationsList' ? (
                <StyledTabs value={selectedTab} onChange={handleTabChange} variant="fullWidth">
                    <Tab
                        data-target-id="relations-modal-content-common-tab"
                        label={t('profile:inCommonCount', {
                            count: user.mutualContactsCount ?? 0,
                            plurial: (user.mutualContactsCount ?? 0) > 1 ? 's' : '',
                        })}
                        value="common"
                    />
                    <Tab
                        data-target-id="relations-modal-content-relations-tab"
                        label={t('profile:relationsCount', {
                            count: user.numberOfContacts,
                            plurial: user.numberOfContacts > 1 ? 's' : '',
                        })}
                        value="all"
                    />
                </StyledTabs>
            ) : (
                <Formik initialValues={{ query: '' }} onSubmit={console.info}>
                    {/* Formik is used here to avoid errors using useField in children components */}
                    <TextFieldWrapper>
                        <TextField
                            data-target-id="relations-modal-content-search"
                            name="search"
                            label={t('common:search')}
                            InputProps={{
                                startAdornment: <Icon name="Search" />,
                            }}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    </TextFieldWrapper>
                </Formik>
            )}
            <Box mx={-3}>
                <Divider />
            </Box>
            <Box
                ref={scrollerContainerRef}
                display="flex"
                flexDirection="column"
                flex={1}
                mx={-1}
                overflow="auto"
                onScroll={handleScroll}
            >
                {!data?.userContactsV2?.results?.length && !loading && (
                    <Box
                        display="flex"
                        flexDirection="column"
                        height="20rem"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Icon color={colours.primary.main} size="7.5rem" name="NoCommonRelations" />
                        <Box mt={-1}>
                            <Typography data-target-id="relations-modal-content-empty" variant="body2" align="center">
                                {t(
                                    selectedTab === 'common'
                                        ? 'profile:noCommonRelations'
                                        : 'profile:relationsEmptyState',
                                )}
                            </Typography>
                        </Box>
                    </Box>
                )}
                {data?.userContactsV2?.results?.map((_user) => {
                    if (!_user) {
                        return null;
                    }

                    return (
                        <div key={_user.id}>
                            <SimpleHorizontalUserCard
                                dataTargetId={`simple-horizontal-card-${_user.id}`}
                                user={_user as User}
                                firstSubtitle={
                                    _user.isProfessionnal
                                        ? null // send null to not display activity in this case
                                        : undefined
                                }
                                secondSubtitle={
                                    _user.id === currentUser?.id
                                        ? undefined // send undefined to not display common relations for current user
                                        : 'commonRelationsCount'
                                }
                                minHeight="5rem"
                                padding="0.5rem 1rem"
                                backgroundColor="transparent"
                                rightArea={
                                    _user.contactStatusV2 !== ContactStatus.Accepted && _user.id !== currentUser?.id ? (
                                        <AddRemoveContact
                                            user={_user as User}
                                            contactStatus={_user.contactStatusV2}
                                            refetchQueries={['UserContacts']}
                                        />
                                    ) : undefined
                                }
                            />
                        </div>
                    );
                })}
                {loading && <Loader />}
            </Box>
        </Box>
    );
};

export default RelationsModalContent;
